import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, X } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import SEOHead from '../../components/SEO/SEOHead';
import DogCard from './DogCard';
import { useAnimals } from '../../contexts/AnimalsContext';
import { PublicAnimal } from '../../services/api';

const Dogs: React.FC = () => {
  const { 
    animals, 
    loading, 
    error, 
    fetchAnimals, 
    totalAnimals, 
    currentPage, 
    totalPages, 
    pagination,
    stats,
    loadStats 
  } = useAnimals();
  
  // Filter states - changed to single values instead of arrays
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedAge, setSelectedAge] = useState<string>('');
  const [filteredDogs, setFilteredDogs] = useState<PublicAnimal[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Pagination - use server-side pagination instead of client-side filtering
  const itemsPerPage = 12;

  // Load animals with filters and pagination
  const loadAnimalsWithFilters = async (page: number = 1) => {
    const params: any = {
      page,
      limit: itemsPerPage
    };

    // Add filters to API params
    if (selectedSize) params.size = selectedSize;
    if (selectedGender) params.gender = selectedGender === 'Mascul' ? 'male' : 'female';
    
    // Add age filter - map age categories to backend values
    if (selectedAge) {
      const ageCategory = selectedAge;
      switch (ageCategory) {
        case 'Puppy':
          params.age = 'puppy';
          break;
        case 'Young':
          params.age = 'young';
          break;
        case 'Adult':
          params.age = 'adult';
          break;
        case 'Senior':
          params.age = 'senior';
          break;
      }
    }
    
    // Add search term to server-side filtering
    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    
    await fetchAnimals(params);
  };

  // Load stats when component mounts
  useEffect(() => {
    loadStats();
  }, []);

  // Set filtered dogs to match fetched animals (no client-side filtering needed)
  useEffect(() => {
    setFilteredDogs(animals);
  }, [animals]);

  // Reload when filters change (server-side, excluding search)
  useEffect(() => {
    loadAnimalsWithFilters(1);
  }, [selectedGender, selectedSize, selectedAge]);

  // Search function triggered by button click or Enter key
  const handleSearch = () => {
    loadAnimalsWithFilters(1);
  };

  // Handle Enter key press on search input
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm('');
    loadAnimalsWithFilters(1);
  };

  const toggleFilter = (value: string, type: 'gender' | 'size' | 'age') => {
    if (type === 'gender') {
      setSelectedGender(prev => prev === value ? '' : value);
    } else if (type === 'size') {
      setSelectedSize(prev => prev === value ? '' : value);
    } else if (type === 'age') {
      setSelectedAge(prev => prev === value ? '' : value);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGender('');
    setSelectedSize('');
    setSelectedAge('');
  };

  const handlePageChange = (page: number) => {
    loadAnimalsWithFilters(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = searchTerm || selectedGender || selectedSize || selectedAge;
  
  // Get the appropriate total count (filtered or overall)
  const displayTotal = hasActiveFilters 
    ? (pagination?.total || filteredDogs.length) 
    : (stats?.total || totalAnimals);

  if (loading) {
    return (
      <>
        <PageHeader
          title="Găsește-ți prietenul perfect"
          subtitle="Toți câinii noștri sunt vaccinați, deparazitați și socializați, gata să găsească o familie iubitoare"
          image="https://images.pexels.com/photos/551628/pexels-photo-551628.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
        />
        <Container>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Se încarcă câinii disponibili pentru adopție...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader
          title="Găsește-ți prietenul perfect"
          subtitle="Toți câinii noștri sunt vaccinați, deparazitați și socializați, gata să găsească o familie iubitoare"
          image="https://images.pexels.com/photos/551628/pexels-photo-551628.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
        />
        <Container>
          <div className="text-center py-12">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 max-w-md mx-auto">
              <p>{error}</p>
            </div>
            <button 
              onClick={() => fetchAnimals({ limit: 20 })}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition-colors"
            >
              Încearcă din nou
            </button>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title="Adopta un caine din Centrul de adoptii Odai - Salveaza o viata"
        description={`Adoptă un câine din ${displayTotal} câini disponibili la Adăpostul Odăi, Sector 1 București. Adopție 100% gratuită, câini vaccinați, microcipați și deparazitați. Găsește-ți compañonul perfect astăzi!`}
        keywords="adopta caine bucuresti, caini pentru adopție, adopta caini sector 1, adaspost caini bucuresti, caini gratis, animale adopție, câini vaccinați adopție, adăpost odăi câini, protecția animalelor câini"
        url="/caini"
        type="website"
        image="https://images.pexels.com/photos/551628/pexels-photo-551628.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />
      <PageHeader
        title="Adopta un caine din Centrul de adoptii Odai"
        subtitle={`Până acum, ${stats?.adopted || 150} câini au fost adoptați și și-au găsit o familie iubitoare. Încă ${stats?.available || 550} de câini așteaptă să fie adoptați și să își găsească un cămin permanent.`}
        image="https://images.pexels.com/photos/551628/pexels-photo-551628.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      <Container>
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center justify-center w-full p-3 bg-primary text-white rounded-lg shadow-md"
            >
              <Filter size={20} className="mr-2" />
              Filtre
              {hasActiveFilters && (
                <span className="ml-2 bg-accent text-white rounded-full px-2 py-1 text-xs">
                  {[searchTerm, selectedGender, selectedSize, selectedAge].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>

          {/* Filters Sidebar */}
          <div 
            className={`${
              isFilterOpen ? 'block' : 'hidden'
            } lg:block w-full lg:w-72 flex-shrink-0`}
          >
            <div className="bg-white rounded-lg shadow-md p-5 sticky top-24">
              <div className="mb-6">
                <h3 className="font-bold text-primary text-lg mb-3">Caută</h3>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Caută după nume, culoare..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                      className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <Search
                      size={18}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                  </div>
                  <button
                    onClick={handleSearch}
                    className="px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
                  >
                    <Search size={18} />
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-primary text-lg mb-3">Gen</h3>
                <div className="space-y-2">
                  {['Mascul', 'Femelă'].map((gender) => (
                    <label key={gender} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        checked={selectedGender === gender}
                        onChange={() => toggleFilter(gender, 'gender')}
                        className="text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-gray-700">{gender}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-primary text-lg mb-3">Talie</h3>
                <div className="space-y-2">
                  {['mic', 'mediu', 'mare'].map((size) => (
                    <label key={size} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="size"
                        checked={selectedSize === size}
                        onChange={() => toggleFilter(size, 'size')}
                        className="text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-gray-700">
                        {size === 'mic' ? 'Mic' : size === 'mediu' ? 'Mediu' : 'Mare'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-primary text-lg mb-3">Vârstă</h3>
                <div className="space-y-2">
                  {[
                    { id: 'Puppy', name: 'Cățel (0-1 ani)' },
                    { id: 'Young', name: 'Tânăr (1-3 ani)' },
                    { id: 'Adult', name: 'Adult (3-8 ani)' },
                    { id: 'Senior', name: 'Senior (8+ ani)' }
                  ].map((age) => (
                    <label key={age.id} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="age"
                        checked={selectedAge === age.id}
                        onChange={() => toggleFilter(age.id, 'age')}
                        className="text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-gray-700">{age.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="w-full flex items-center justify-center p-2 text-gray-600 hover:text-primary border border-gray-300 rounded-md hover:border-primary transition-colors"
                >
                  <X size={16} className="mr-2" />
                  Șterge filtrele
                </button>
              )}
            </div>
          </div>

          {/* Dogs Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary">
                Afișare {Math.min(filteredDogs.length, itemsPerPage)} din {displayTotal} câini disponibili pentru adopție
              </h2>
            </div>

            {filteredDogs.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                  <Search size={40} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">
                  Nu am găsit câini care să corespundă criteriilor tale
                </h3>
                <p className="text-gray-600 mb-4">
                  Încearcă să modifici filtrele sau să cauți după alți termeni.
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors"
                  >
                    Șterge toate filtrele
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredDogs.map((dog, index) => (
                    <motion.div
                      key={dog.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <DogCard dog={dog} />
                    </motion.div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center mt-12 space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Anterior
                    </button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-md text-sm font-medium ${
                              pageNum === currentPage
                                ? 'bg-primary text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Următor
                    </button>
                  </div>
                )}

                {/* Mobile Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center mt-8 sm:hidden">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Anterior
                      </button>
                      
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                        {currentPage}/{totalPages}
                      </span>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Următor
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default Dogs;